import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { defaultConfig } from '../utils/config';
import { GithubFormat } from '../pages/game/utils/formatCode';
import { getPublicUrl } from './storageHelpers';
import { useGameMetrics } from './useGameMetrics';
import { generateHistoryEntryId, uploadCodeToStorage, downloadCodeFromStorage } from './codeStorage';

export type Game = Database['public']['Tables']['games']['Row'] & {
  user_tag?: string;
};
export type GameHistoryEntry = Omit<Database['public']['Tables']['game_history_entries']['Row'], 'code'> & {
  code: GithubFormat;
  code_url?: string; // Public URL to code file in storage
  data: { plan: string, stage: string, stageType: string; };
  user_facing: boolean | null;
  status: GameHistoryStatusType;
  game_art_snapshot?: Database['public']['Tables']['game_art']['Row'][] | null;
};

export type GameHistoryStatusType = 'IN_PROGRESS' | 'SUCCESS' | 'ERROR' | 'CANCELLED';

export type PlanData = { plan: string, stage: string, stageType: string; };

export type GameWithHistory = Game & {
  game_history_entries: GameHistoryEntry[];
};

export const parseCodeFromDB = (codeString: string): GithubFormat => {
  try {
    return JSON.parse(codeString) as GithubFormat;
  } catch (e) {
    console.error('Error parsing code from DB:', e);
    throw new Error('Invalid code format in database');
  }
};

const stringifyCodeForDB = (code: GithubFormat): string => {
  return JSON.stringify(code);
};

// Helper function to determine game history status (with precedence to correct historical false positives)
const getGameHistoryStatus = (game: GameWithHistory): GameWithHistory => {
  if (!game.game_history_entries) return game;
  
  game.game_history_entries = game.game_history_entries.map(entry => {
    const stage = entry?.data?.stage || '';
    const dbStatus = (entry.status as GameHistoryStatusType | undefined);

    // Default to IN_PROGRESS
    let status: GameHistoryStatusType = 'IN_PROGRESS';

    // Preserve explicit CANCELLED from DB (do not override)
    if (dbStatus === 'CANCELLED') {
      status = 'CANCELLED';
    } else {
      // If user-facing and not CANCELLED, prefer SUCCESS
      if (entry.user_facing === true) {
        status = 'SUCCESS';
      }
      // If plan indicates failure, override to ERROR
      if (stage && stage.includes('Failed')) {
        status = 'ERROR';
      }
    }
    
    const updatedHistoryEntry = {
      ...entry,
      status: status
    };
    // console.log('Updated history entry:', updatedHistoryEntry);
    return updatedHistoryEntry;
  });
  
  // console.log('Updated game history statuses:', game);
  return game;
};

// Constants for storage
const STORAGE_BUCKET = 'playmix-assets';

export const useGame = () => {
const gameMetrics = useGameMetrics();

  const createGame = useCallback(async (gameName: string): Promise<Game> => {
    // console.log('Creating new game with name:', gameName);
    
    const { data: game, error: gameError } = await supabase
      .from('games')
      .insert({
        game_name: gameName,
      })
      .select('*')
      .single();

    if (gameError) {
      console.error('Error creating game:', gameError);
      throw gameError;
    }
    if (!game) {
      console.error('No game returned after creation');
      throw new Error('No game returned after creation');
    }
    
    // console.log('Successfully created game:', game);
    return game;
  }, []);


  const updateGame = useCallback(async (gameId: string, updates: Partial<Game>): Promise<Game> => {
    const { data: game, error: gameError } = await supabase
      .from('games')
      .update(updates)
      .eq('id', gameId)
      .select('*')
      .single();

    if (gameError) throw gameError;
    if (!game) throw new Error('No game returned after update');
    return game;
  }, []);

  const createHistoryEntry = useCallback(async (
    gameId: string,
    prompt: string,
    code: GithubFormat,
    data: { plan: string, stage?: string, stageType?: string },
    status: GameHistoryEntry['status'],
    user_facing: boolean | null,
  ): Promise<GameHistoryEntry> => {
    // Get previous entry data for fallback
    const { data: previousEntries } = await supabase
      .from('game_history_entries')
      .select('id, data')
      .eq('game_id', gameId)
      .order('updated_at', { ascending: false })
      .limit(1);
      
    const previousEntry = previousEntries && previousEntries.length > 0 ? previousEntries[0] : null;

    // Process the data parameter - it can be either a string or an object
    const dataToInsert = typeof data === 'string' 
      ? { plan: data, stage: '', stageType: '' }
      : data;

    if (defaultConfig.USE_CODE_FROM_STORAGE) {
      // NEW: Generate UUID → upload to storage → insert with code_url
      const entryId = generateHistoryEntryId();
      const codeUrl = await uploadCodeToStorage(entryId, gameId, code);
      
      const { data: entry, error: entryError } = await supabase
        .from('game_history_entries')
        .insert({
          id: entryId,
          game_id: gameId,
          prompt,
          code_url: codeUrl,
          data: dataToInsert || previousEntry?.data,
          status,
          user_facing,
        })
        .select('id, created_at, updated_at, game_id, prompt, code_url, data, status, user_facing, deleted')
        .single();

      if (entryError) throw entryError;
      if (!entry) throw new Error('No entry returned after creation');
      
      return {
        ...entry,
        code: code,
        code_url: codeUrl,
      } as GameHistoryEntry;
    } else {
      // LEGACY: Insert with code column
      const { data: entry, error: entryError } = await supabase
        .from('game_history_entries')
        .insert({
          game_id: gameId,
          prompt,
          code: stringifyCodeForDB(code),
          data: dataToInsert || previousEntry?.data,
          status,
          user_facing,
        })
        .select('id, created_at, updated_at, game_id, prompt, code, data, status, user_facing, deleted')
        .single();

      if (entryError) throw entryError;
      if (!entry) throw new Error('No entry returned after creation');
      
      return {
        ...entry,
        code: parseCodeFromDB(entry.code)
      } as GameHistoryEntry;
    }
  }, []);

  const updateHistoryEntry = useCallback(async (
    entryId: string,
    updates: Partial<Omit<GameHistoryEntry, 'code'>>
  ): Promise<GameHistoryEntry> => {
    if (defaultConfig.USE_CODE_FROM_STORAGE) {
      // NEW: Query with code_url, download from storage
      const { data: entry, error: entryError } = await supabase
        .from('game_history_entries')
        .update(updates)
        .eq('id', entryId)
        .select('id, created_at, updated_at, game_id, prompt, code_url, data, status, user_facing, deleted')
        .single();

      if (entryError) throw entryError;
      if (!entry) throw new Error('No entry returned after update');
      
      let code: GithubFormat = [];
      if (entry.code_url) {
        try {
          code = await downloadCodeFromStorage(entry.code_url);
        } catch (e) {
          console.error('Error loading code for entry:', entry.id, e);
        }
      }
      
      return {
        ...entry,
        code: code,
      } as GameHistoryEntry;
    } else {
      // LEGACY: Query with code column
      const { data: entry, error: entryError } = await supabase
        .from('game_history_entries')
        .update(updates)
        .eq('id', entryId)
        .select('id, created_at, updated_at, game_id, prompt, code, data, status, user_facing, deleted')
        .single();

      if (entryError) throw entryError;
      if (!entry) throw new Error('No entry returned after update');
      
      return {
        ...entry,
        code: parseCodeFromDB(entry.code)
      } as GameHistoryEntry;
    }
  }, []);

  const getGame = useCallback(async (gameId: string): Promise<GameWithHistory | null> => {
    if (defaultConfig.USE_CODE_FROM_STORAGE) {
      // NEW: Select code_url, download from storage
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select(`
          *,
          game_history_entries (${defaultConfig.USE_NEW_GAME_ART_HISTORY ? 'id, created_at, updated_at, game_id, prompt, code_url, data, status, user_facing, game_art_snapshot' : 'id, created_at, updated_at, game_id, prompt, code_url, data, status, user_facing'}),
          profiles!games_user_id_fkey (user_tag)
        `)
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;
      if (!game) return null;
      
      game.user_tag = '@' + game.profiles?.user_tag;
      delete game.profiles;

      if (game.game_history_entries) {
        const entriesWithCode = await Promise.all(
          game.game_history_entries.map(async (entry: any) => {
            let code: GithubFormat = [];
            if (entry.code_url) {
              try {
                code = await downloadCodeFromStorage(entry.code_url);
              } catch (e) {
                console.error('Error loading code for entry:', entry.id, e);
              }
            }
            return {
              ...entry,
              code: code,
              status: 'IN_PROGRESS' as const,
            };
          })
        );
        game.game_history_entries = entriesWithCode;
      }
      
      if (game.game_history_entries) {
        game.game_history_entries.sort((a: any, b: any) => 
          new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
        );
      }
      
      const gameWithStatus = getGameHistoryStatus(game as GameWithHistory);
      return gameWithStatus;
    } else {
      // LEGACY: Select code column directly
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select(`
          *,
          game_history_entries (${defaultConfig.USE_NEW_GAME_ART_HISTORY ? 'id, created_at, updated_at, game_id, prompt, code, data, status, user_facing, game_art_snapshot' : 'id, created_at, updated_at, game_id, prompt, code, data, status, user_facing'}),
          profiles!games_user_id_fkey (user_tag)
        `)
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;
      if (!game) return null;
      
      game.user_tag = '@' + game.profiles?.user_tag;
      delete game.profiles;

      if (game.game_history_entries) {
        game.game_history_entries = game.game_history_entries.map((entry: any) => ({
          ...entry,
          code: parseCodeFromDB(entry.code),
          status: 'IN_PROGRESS'
        }));
      }
      
      if (game.game_history_entries) {
        game.game_history_entries.sort((a: any, b: any) => 
          new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
        );
      }
      
      const gameWithStatus = getGameHistoryStatus(game as GameWithHistory);
      return gameWithStatus;
    }
  }, []);

  const subscribeToGameHistoryUpdates = useCallback((gameId: string, onNewEntry: () => void) => {
    // console.log("Subscribing history updates", gameId);

    if (!gameId) {
      // console.warn("Not subscribed - no game id");
      return () => {

      };
    }

    //supabase.removeAllChannels();

    // Use a more specific channel name with a timestamp to avoid conflicts
    const channelName = `game-history-${gameId}`;
    // console.log("Creating channel:", channelName);

    const channel = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'game_history_entries',
              filter: `game_id=eq.${gameId}`, // Make sure column name matches exactly
            },
            (payload) => {
              // console.log("[DB Subscription] Received history update event:", payload.eventType, 'record id:', (payload.new as any)?.id);
              onNewEntry();
              // console.log("called payload callback");
            }
        )
        .subscribe((status, two) => {
          // console.log(`Subscription status for ${channelName}:`, status, two);
        }, 120000);

    // console.log("Subscribed to history updates");

    return () => {
      // console.log("Cleaning up subscription for:", channelName);
      supabase.removeChannel(channel);
    };
  }, []);

  const remixGame = useCallback(async (gameId: string, historyId?: string): Promise<GameWithHistory> => {
    // Get the original game with its history entries
    const originalGame = await getGame(gameId);
    if (!originalGame) {
      throw new Error('Game not found');
    }

    // Determine which history entry to remix
    let entryToRemix: GameHistoryEntry | undefined;
    if (historyId) {
      entryToRemix = originalGame.game_history_entries.find(entry => entry.id === historyId);
      if (!entryToRemix) {
        throw new Error(`History entry with ID ${historyId} not found`);
      }
      // console.log('[useGame.remixGame] Using provided historyId', { gameId, historyId });
    } else {
      // Find the last user-facing history entry
      const userFacingEntries = originalGame.game_history_entries.filter(entry => entry.user_facing === true);
      if (userFacingEntries.length === 0) {
        throw new Error('No user-facing history entries found');
      }
      entryToRemix = userFacingEntries[userFacingEntries.length - 1];
      // console.log('[useGame.remixGame] Using latest user-facing history', { gameId, historyId: entryToRemix.id });
    }
    // console.log("entryToRemix", entryToRemix);

    // Create a new game with a modified name
    const newGameName = originalGame.game_name;
    const newGame = await createGame(newGameName);

    // Set the original game ID to establish the relationship and hide by default
    await updateGame(newGame.id, {
      original_game_id: gameId,
      hidden: true
    });

    // Copy the screenshot if it exists
    if (originalGame.screenshot_url) {
      await updateGame(newGame.id, {
        screenshot_url: originalGame.screenshot_url
      });
    }

    // Populate game_art for the remixed game
    if (historyId) {
      // When a historyId is provided, always populate from the snapshot of that entry (even if empty)
      try {
        const snapshot = (entryToRemix.game_art_snapshot || []) as Database['public']['Tables']['game_art']['Row'][];
        if (Array.isArray(snapshot) && snapshot.length > 0) {
          const snapshotInserts = snapshot.map((art) => ({
            created_at: art.created_at,
            game_id: newGame.id,
            asset_url: art.asset_url,
            asset_name: art.asset_name,
            asset_description: art.asset_description,
            code_file_name: art.code_file_name,
            art_id: art.art_id,
          }));
          const { error: insertError } = await supabase
            .from('game_art')
            .insert(snapshotInserts);
          if (insertError) {
            console.error('[useGame.remixGame] Error inserting snapshot game_art:', insertError);
          } else {
            // console.log('[useGame.remixGame] Restored game_art from snapshot', {
            //   gameId,
            //   historyId,
            //   inserted: snapshotInserts.length,
            // });
          }
        } else {
          // console.log('[useGame.remixGame] Snapshot empty - no game_art inserted from snapshot', { gameId, historyId });
        }
      } catch (error) {
        console.error('[useGame.remixGame] Error during snapshot game_art handling:', error);
        // Continue with the remix process even if art population fails
      }
    } else {
      // When no historyId is provided, copy current game_art from the original game
      try {
        const { data: gameArts, error: gameArtsError } = await supabase
          .from('game_art')
          .select('*')
          .order('created_at', { ascending: true })
          .eq('game_id', gameId);
        
        if (gameArtsError) {
          console.error('Error fetching game art entries:', gameArtsError);
        } else if (gameArts && gameArts.length > 0) {
          const gameArtInserts = gameArts.map(art => ({
            created_at: art.created_at,
            game_id: newGame.id,
            asset_url: art.asset_url,
            asset_name: art.asset_name,
            asset_description: art.asset_description,
            code_file_name: art.code_file_name,
            art_id: art.art_id,
          }));
          const { error: insertError } = await supabase
            .from('game_art')
            .insert(gameArtInserts);
          if (insertError) {
            console.error('Error copying game art entries:', insertError);
          }
        }
      } catch (error) {
        console.error('Error during game art copying:', error);
        // Continue with the remix process even if art copying fails
      }
    }

    // Create a new history entry with the copied code and data
    const dataToInsert = {
      plan: entryToRemix.data?.plan || '',
      stage: entryToRemix.data?.stage || '',
      stageType: entryToRemix.data?.stageType || ''
    };
    
    const newHistoryEntry = await createHistoryEntry(
      newGame.id,
      entryToRemix.prompt,
      entryToRemix.code,
      dataToInsert, // Pass the properly structured data
      entryToRemix.status,
      true // Set as user-facing
    );

    // Format the new game with history to match getGame formatting
    const formattedGame: GameWithHistory = {
      ...newGame,
      game_history_entries: [{
        ...newHistoryEntry,
        // Ensure code is properly parsed
        code: typeof newHistoryEntry.code === 'string' 
          ? parseCodeFromDB(newHistoryEntry.code as unknown as string) 
          : newHistoryEntry.code,
        data: newHistoryEntry.data,
      }]
    };

    // console.log("formattedGame", formattedGame);
    return formattedGame;
  }, [createGame, updateGame, createHistoryEntry, getGame]);

  const saveCodeToHistory = useCallback(async (
    gameId: string,
    newCode: GithubFormat,
    prompt: string
  ): Promise<GameHistoryEntry> => {
    // Get the game with its history entries
    const game = await getGame(gameId);
    if (!game) {
      throw new Error('Game not found');
    }

    // Find the most recent user-facing history entry
    const userFacingEntries = game.game_history_entries.filter(entry => entry.user_facing === true);
    if (userFacingEntries.length === 0) {
      throw new Error('No user-facing history entries found');
    }
    
    // Sort by updated_at in descending order and get the most recent one
    userFacingEntries.sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
    const mostRecentEntry = userFacingEntries[0];

    // console.log('Copying history entry', mostRecentEntry);
    
    // Create a new history entry with the same data but new code
    const newHistoryEntry = await createHistoryEntry(
      gameId,
      prompt,
      newCode,
      mostRecentEntry.data,
      mostRecentEntry.status,
      true // Set as user-facing
    );

    // console.log('New history entry created', newHistoryEntry);
    
    return newHistoryEntry;
  }, [getGame, createHistoryEntry]);

  const savePlanToHistory = useCallback(async (
    historyId: string,
    gameId: string,
    prompt: string,
    newPlanStatus: string,
    newPlanStatusType: string,
    status: GameHistoryStatusType,
    userFacing: boolean,
  ): Promise<GameHistoryEntry> => {
    // Get the game with its history entries
    const game = await getGame(gameId);
    if (!game) {
      throw new Error('Game not found');
    }

    // Find the specified history entry
    const entryToCopy = game.game_history_entries.find(entry => entry.id === historyId);
    if (!entryToCopy) {
      throw new Error(`History entry with ID ${historyId} not found`);
    }

    const originalPlanData = entryToCopy.data;
    // console.log('Original plan: ', originalPlanData);

    const finalPlanData = {
      plan: originalPlanData?.plan || '', // Use the existing plan content
      stage: newPlanStatus, // Use the new status
      stageType: newPlanStatusType, // Use the new status type
    };

    // Create a new history entry with the code from the found entry and the updated plan data
    const newHistoryEntry = await createHistoryEntry(
      gameId,
      prompt,
      entryToCopy.code,
      finalPlanData, // Pass the new combined plan data
      status,
      userFacing
    );

    // console.log('New history entry created', newHistoryEntry);
    
    return newHistoryEntry;
  }, [getGame, createHistoryEntry]);

  return {
    createGame,
    updateGame,
    createHistoryEntry,
    updateHistoryEntry,
    getGame,
    subscribeToGameHistoryUpdates,
    remixGame,
    saveCodeToHistory,
    savePlanToHistory,
  };
};